import { GetObjectCommand, HeadObjectCommand, S3Client } from '@aws-sdk/client-s3';
import { logger } from 'src/logger';
import { CognitoService } from 'src/services';

let cachedS3Client: S3Client | null = null;

/**
 * Parses an S3 URI and extracts the bucket and key.
 * @param s3URI The S3 URI (e.g., s3://bucket-name/path/to/file.png)
 * @returns An object containing the bucket and s3Key
 * @throws Will throw an error if the URI format is invalid
 */
export const parseS3URI = (s3URI: string): { bucket: string; s3Key: string } => {
  if (!s3URI || !s3URI.startsWith('s3://')) {
    throw new Error(`Invalid S3 URI format: ${s3URI}. Expected format: s3://bucket-name/key`);
  }

  const uriWithoutProtocol = s3URI.substring(5); // Remove 's3://'
  const firstSlashIndex = uriWithoutProtocol.indexOf('/');

  if (firstSlashIndex === -1) {
    throw new Error(`Invalid S3 URI format: ${s3URI}. No key found after bucket name.`);
  }

  const bucket = uriWithoutProtocol.substring(0, firstSlashIndex);
  const s3Key = uriWithoutProtocol.substring(firstSlashIndex + 1);

  if (!bucket || !s3Key) {
    throw new Error(`Invalid S3 URI format: ${s3URI}. Bucket and key are required.`);
  }

  return { bucket, s3Key };
};

/**
 * Creates and returns an S3 client with the configured region and credentials.
 * @returns A configured S3Client instance
 */
export const getS3Client = (): S3Client | undefined => {
  try {
    if (cachedS3Client) return cachedS3Client;

    const REGION = 'us-west-2';
    const actualRegion = REGION.includes('{{') ? 'us-west-2' : REGION;

    if (REGION.includes('{{')) {
      logger.warn('S3 region template variable was not replaced during build, using fallback region', {
        templateValue: REGION,
        fallbackRegion: actualRegion
      });
    }

    cachedS3Client = new S3Client({
      region: actualRegion,
      credentials: CognitoService.credentialProvider
    });
    return cachedS3Client;
  } catch (error) {
    logger.error('Failed to create S3 client', {
      error: error instanceof Error ? error.message : String(error)
    });
    return undefined;
  }
};

/**
 * Downloads an image from S3 using its URI.
 * @param s3URI The S3 URI of the image to download (e.g., s3://bucket-name/avatars/user/large.png).
 * @param signal Optional AbortSignal for cancelling the request
 * @returns The image data as an ArrayBuffer, or null if not found.
 * @throws Will throw an error if there is an issue downloading the file.
 */
export const getImageFromS3URI = async (s3URI: string, signal?: AbortSignal): Promise<ArrayBuffer | null> => {
  try {
    const s3Client = getS3Client();

    if (!s3Client) {
      throw new Error('Failed to initialize S3 client');
    }

    const { bucket, s3Key } = parseS3URI(s3URI);

    // Check if aborted before making requests
    if (signal?.aborted) {
      throw new Error('Request was aborted');
    }

    // Check if object exists
    const headCommand = new HeadObjectCommand({
      Bucket: bucket,
      Key: s3Key
    });

    try {
      await s3Client.send(headCommand, { abortSignal: signal });
    } catch (headError: any) {
      if (headError.name === 'NotFound' || headError.$metadata?.httpStatusCode === 404) {
        logger.warn('Image not found in S3', { s3URI, bucket, s3Key });
        return null;
      }
      throw headError;
    }

    // Check if aborted before getting file
    if (signal?.aborted) {
      throw new Error('Request was aborted');
    }

    // Get the file
    const getCommand = new GetObjectCommand({
      Bucket: bucket,
      Key: s3Key,
      ResponseCacheControl: 'no-cache'
    });

    const getResponse = await s3Client.send(getCommand, { abortSignal: signal });

    // Check if aborted before processing response
    if (signal?.aborted) {
      throw new Error('Request was aborted');
    }

    if (getResponse.Body) {
      // Convert the stream to ArrayBuffer for image data
      const bodyBytes = await getResponse.Body.transformToByteArray();
      // Create a new ArrayBuffer from the Uint8Array to ensure proper type
      const arrayBuffer = new ArrayBuffer(bodyBytes.length);
      new Uint8Array(arrayBuffer).set(bodyBytes);
      return arrayBuffer;
    } else {
      logger.warn('Empty response body from S3', { s3URI, bucket, s3Key });
      return null;
    }
  } catch (error: any) {
    // Convert abort errors to a standard format
    if (signal?.aborted || error.name === 'AbortError') {
      const abortError = new Error('Request was aborted');
      abortError.name = 'AbortError';
      throw abortError;
    }

    logger.error('Error while downloading image from S3', { s3URI, error: error.message || error });
    throw error;
  }
};
